import { useState, useEffect } from "react";
import { FiArrowUp } from "react-icons/fi";

function BackToTop() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const toggleVisibility = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      setScrollProgress(progress);
      setIsVisible(scrollTop > 300);
    };

    window.addEventListener("scroll", toggleVisibility);
    window.addEventListener("resize", toggleVisibility);
    toggleVisibility(); // Check on mount
    
    return () => {
      window.removeEventListener("scroll", toggleVisibility);
      window.removeEventListener("resize", toggleVisibility);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const circumference = 2 * Math.PI * 25; // radius is 25 (adjusted to prevent clipping)
  const strokeDashoffset = circumference - (scrollProgress / 100) * circumference;

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-8 right-8 z-50">
      {/* Glowing shadow background */}
      <div 
        className="absolute inset-0 rounded-full blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-300 -z-10"
        style={{
          background: `radial-gradient(circle, rgba(255, 220, 124, 0.63) 0%, rgba(246, 196, 59, 0.4) 40%, transparent 70%)`,
          width: '80px',
          height: '80px',
          transform: 'translate(3px, 3px)',
        }}
      />
      {/* Pulsing glow effect */}
      <div 
        className="absolute inset-0 rounded-full blur-2xl opacity-30 animate-pulse -z-10"
        style={{
          background: `radial-gradient(circle, rgba(246, 196, 59, 0.4) 0%, transparent 60%)`,
          width: '90px',
          height: '90px',
          transform: 'translate(-5px, -5px)',
        }}
      />
      {/* Main button */}
      <button
        onClick={scrollToTop}
        // FIXED: Changed from-blue-600 to-blue-700 -> from-yellow-400 to-yellow-500
        className="relative w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 rounded-full transition-all duration-300 flex items-center justify-center group backdrop-blur-sm border border-yellow-400/20 overflow-visible"
        style={{
          boxShadow: `
            0 10px 25px -5px rgba(246, 196, 59, 0.4),
            0 8px 10px -6px rgba(246, 196, 59, 0.4),
            0 0 0 1px rgba(246, 196, 59, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.4)
          `,
        }}
        aria-label="Back to top"
        title="Scroll to top"
      >
      {/* Progress ring */}
      <svg
        className="absolute inset-0 w-full h-full transform -rotate-90 overflow-visible"
        viewBox="0 0 56 56"
        width="56"
        height="56"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background circle */}
        <circle
          cx="28"
          cy="28"
          r="25"
          stroke="rgba(255, 255, 255, 0.3)"
          strokeWidth="2"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="28"
          cy="28"
          r="25"
          // FIXED: Changed stroke to dark brown so it's visible on yellow
          stroke="rgba(113, 63, 18, 0.8)" 
          strokeWidth="2.5"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-100 ease-out"
          style={{
            filter: "drop-shadow(0 0 3px rgba(246, 196, 59, 0.4))",
          }}
        />
      </svg>

      {/* Arrow icon */}
      <FiArrowUp className="text-black text-xl relative z-10 group-hover:-translate-y-0.5 transition-transform duration-200" />
      
      {/* Pulse effect */}
      {/* FIXED: Changed bg-blue-400/20 -> bg-yellow-400/20 */}
      <div className="absolute inset-0 rounded-full bg-yellow-400/20 animate-ping opacity-0 group-hover:opacity-100 group-hover:animate-none transition-opacity" />
      
      {/* Hover shadow enhancement */}
      <div 
        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"
        style={{
          filter: 'blur(15px)',
          background: 'radial-gradient(circle, rgba(246, 196, 59, 0.4) 0%, transparent 70%)',
          width: '100px',
          height: '100px',
          transform: 'translate(-18px, -18px)',
        }}
      />
    </button>
    </div>
  );
}

export default BackToTop;